/**
 * "Open as tab / new window" control for the messaging surfaces (Messaging
 * workspace header, Inbox header, Mailbox toolbar, command palette).
 *
 * "Open as tab" adds a tab to this window's own strip (the main window's
 * MainWindowTabs). It is offered only when there is such a strip — there is no
 * "open in a new browser tab" any more.
 *
 * Renders nothing inside a messaging pop-out — there the tab strip's own
 * "+" and "pop out" actions do this job.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, ExternalLink, Plus } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  hasInAppTabHandler,
  isInMessagingPopout,
  openMessagingPopout,
  type MessagingTabSpec,
  type OpenMode,
} from '@/lib/popout';

interface OpenInNewMenuProps {
  /** What to open — read at click time so it reflects the current view. */
  getTabs: () => { tabs: MessagingTabSpec[]; active?: string | null };
  className?: string;
}

/** Only when this window has its own tab strip; read when the menu opens (the content mounts then). */
function OpenAsTabItem({ onSelect }: { onSelect: () => void }) {
  const { t } = useTranslation();
  if (!hasInAppTabHandler()) return null;
  return (
    <DropdownMenuItem onSelect={onSelect} className="gap-2" data-testid="open-in-new-tab">
      <Plus className="h-4 w-4" />
      {t('mainTabs.open_as_tab', 'Open as tab')}
    </DropdownMenuItem>
  );
}

export function OpenInNewMenu({ getTabs, className = '' }: OpenInNewMenuProps) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!error) return;
    const id = window.setTimeout(() => setError(null), 6000);
    return () => window.clearTimeout(id);
  }, [error]);

  if (isInMessagingPopout()) return null;

  const open = async (mode: OpenMode) => {
    setError(null);
    const result = await openMessagingPopout(getTabs(), mode);
    if (!result.ok) {
      setError(
        result.error === 'popup blocked'
          ? t('messagingPopout.popup_blocked', 'Your browser blocked the new window. Allow pop-ups for this site and try again.')
          : t('messagingPopout.open_failed', 'Could not open a new window.'),
      );
    }
  };

  const label = t('messagingPopout.open_in_new', 'Open in new tab or window');

  return (
    <div className={`relative inline-flex ${className}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-slate-100 hover:text-foreground dark:hover:bg-slate-800"
            aria-label={label}
            title={label}
          >
            <ExternalLink className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <OpenAsTabItem onSelect={() => void open('tab')} />
          <DropdownMenuItem onSelect={() => void open('window')} className="gap-2" data-testid="open-in-new-window">
            <AppWindow className="h-4 w-4" />
            {t('messagingPopout.open_new_window', 'Open in new window')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {error && (
        <div
          role="alert"
          className="absolute right-0 top-full z-50 mt-1 w-64 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 shadow dark:border-red-900 dark:bg-red-950 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default OpenInNewMenu;
